import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
  LuX,
  LuCheckCircle2,
  LuAlertCircle,
  LuAlertTriangle,
  LuInfo,
} from 'react-icons/lu';
import clx from 'classnames';
import { APP_BG } from '@/constant/theme/colors.constant';
import { Toast } from './ToastProvider';

// ─── Per-variant design tokens ────────────────────────────────────────────────
const VARIANTS = {
  Success: {
    strip: 'bg-emerald-500',
    iconBg: 'bg-emerald-50 dark:bg-emerald-500/10',
    iconColor: 'text-emerald-500',
    titleCls: 'text-emerald-600 dark:text-emerald-400',
    progress: 'bg-emerald-500',
    closeFg: 'hover:text-emerald-600 dark:hover:text-emerald-400',
    closeBg: 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10',
    label: 'Success',
    Icon: LuCheckCircle2,
  },
  Error: {
    strip: 'bg-red-500',
    iconBg: 'bg-red-50 dark:bg-red-500/10',
    iconColor: 'text-red-500',
    titleCls: 'text-red-600 dark:text-red-400',
    progress: 'bg-red-500',
    closeFg: 'hover:text-red-600 dark:hover:text-red-400',
    closeBg: 'hover:bg-red-50 dark:hover:bg-red-500/10',
    label: 'Error',
    Icon: LuAlertCircle,
  },
  Warning: {
    strip: 'bg-amber-500',
    iconBg: 'bg-amber-50 dark:bg-amber-500/10',
    iconColor: 'text-amber-500',
    titleCls: 'text-amber-600 dark:text-amber-400',
    progress: 'bg-amber-500',
    closeFg: 'hover:text-amber-600 dark:hover:text-amber-400',
    closeBg: 'hover:bg-amber-50 dark:hover:bg-amber-500/10',
    label: 'Warning',
    Icon: LuAlertTriangle,
  },
  Info: {
    strip: 'bg-blue-500',
    iconBg: 'bg-blue-50 dark:bg-blue-500/10',
    iconColor: 'text-blue-500',
    titleCls: 'text-blue-600 dark:text-blue-400',
    progress: 'bg-blue-500',
    closeFg: 'hover:text-blue-600 dark:hover:text-blue-400',
    closeBg: 'hover:bg-blue-50 dark:hover:bg-blue-500/10',
    label: 'Info',
    Icon: LuInfo,
  },
} as const;

export type Truncate =
  | 'truncate-1-lines'
  | 'truncate-2-lines'
  | 'truncate-3-lines';

export type ToastMessageProps = {
  id: string;
  lifetime?: number;
  onRemove?: (id: string) => void;
  truncate?: Truncate;
} & Toast;

// ─── Component ────────────────────────────────────────────────────────────────
export default function ToastMessage({
  id,
  header,
  message,
  lifetime,
  onRemove,
  truncate = 'truncate-2-lines',
  icon,
  type,
}: ToastMessageProps) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startedAt = useRef(0);
  const remaining = useRef(lifetime ?? 4000);

  const [progress, setProgress] = useState(100);
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const V = type ? VARIANTS[type] : null;
  const CustomIcon = icon as React.ElementType | undefined;

  // mount slide-in
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 15);
    return () => clearTimeout(t);
  }, []);

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onRemove?.(id), 320);
  }, [id, onRemove]);

  const startTick = useCallback(() => {
    const total = remaining.current;
    startedAt.current = Date.now();
    rafRef.current = setInterval(() => {
      const pct = Math.max(
        0,
        100 - ((Date.now() - startedAt.current) / total) * 100,
      );
      setProgress(pct);
    }, 16);
  }, []);

  const stopTick = useCallback(() => {
    if (rafRef.current) {
      clearInterval(rafRef.current);
      rafRef.current = null;
    }
    remaining.current = Math.max(
      0,
      remaining.current - (Date.now() - startedAt.current),
    );
  }, []);

  const startTimer = useCallback(() => {
    if (!lifetime || type === 'Error') return;
    startTick();
    timerRef.current = setTimeout(dismiss, remaining.current);
  }, [lifetime, type, startTick, dismiss]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    stopTick();
  }, [stopTick]);

  useEffect(() => {
    startTimer();
    return () => {
      clearTimer();
      if (rafRef.current) clearInterval(rafRef.current);
    };
  }, [startTimer, clearTimer]);

  const gone = leaving || !visible;
  const slideOut = 'translateX(calc(100% + 32px)) scale(0.96)';
  const slideIn = 'translateX(0) scale(1)';
  const txLeave =
    'transform 0.28s cubic-bezier(0.55,0,1,0.45), opacity 0.2s ease-in';
  const txEnter =
    'transform 0.44s cubic-bezier(0.34,1.36,0.64,1), opacity 0.28s ease-out';

  return (
    <div
      role="alert"
      aria-live="assertive"
      onMouseEnter={clearTimer}
      onMouseLeave={startTimer}
      style={{
        transform: gone ? slideOut : slideIn,
        opacity: gone ? 0 : 1,
        transition: leaving ? txLeave : txEnter,
      }}
      className={`relative flex w-full overflow-hidden rounded-2xl ${APP_BG.card} border border-gray-200/70 dark:border-white/[0.07] shadow-2xl shadow-black/[0.12] dark:shadow-black/60 cursor-default select-none`}
    >
      {/* Left accent strip */}
      {V && <div className={clx('w-[3px] shrink-0 self-stretch', V.strip)} />}

      {/* Body */}
      <div className="flex flex-1 items-start gap-3 px-4 py-3.5 pr-11 min-w-0">
        {/* Icon bubble */}
        {V ? (
          <div
            className={clx(
              'shrink-0 flex items-center justify-center w-9 h-9 rounded-xl mt-0.5',
              V.iconBg,
            )}
          >
            <V.Icon size={18} strokeWidth={2} className={V.iconColor} />
          </div>
        ) : CustomIcon ? (
          <div className="shrink-0 flex items-center justify-center w-9 h-9 rounded-xl mt-0.5 bg-gray-100 dark:bg-white/[0.06] text-gray-500">
            <CustomIcon size={18} />
          </div>
        ) : null}

        {/* Text */}
        <div className="flex-1 min-w-0 flex flex-col gap-1 pt-0.5">
          <span
            className={clx(
              'text-[10px] font-black uppercase tracking-[0.12em] leading-none',
              V ? V.titleCls : 'text-gray-500 dark:text-gray-400',
            )}
          >
            {V ? V.label : (header ?? 'Notification')}
          </span>
          <div
            className={clx(
              'text-[13px] leading-relaxed text-gray-700 dark:text-gray-300 break-words',
              typeof message === 'string' && truncate,
            )}
          >
            {message}
          </div>
        </div>
      </div>

      {/* Close button */}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={(e) => {
          e.stopPropagation();
          dismiss();
        }}
        className={clx(
          'absolute top-3 right-3 flex items-center justify-center w-6 h-6 rounded-lg',
          'text-gray-400 dark:text-gray-500 transition-all duration-150',
          V
            ? clx(V.closeBg, V.closeFg)
            : 'hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-600',
        )}
      >
        <LuX size={13} strokeWidth={2.5} />
      </button>

      {/* Progress bar — hidden for Error (persists until manual dismiss) */}
      {lifetime && type !== 'Error' && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gray-100 dark:bg-white/[0.05]">
          <div
            className={clx(
              'h-full transition-none',
              V ? V.progress : 'bg-gray-400',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
